/**
 * DataExportService - Centralized data processing for export functionality
 * 
 * This service extracts and centralizes the data processing logic from JSON exports
 * to enable reuse across different export formats (JSON, Excel, CSV, etc.).
 * 
 * The logic is extracted from the existing handleExport3Topf() and handleExport1Topf()
 * functions in Schritt3.tsx to ensure consistency across export formats.
 * 
 * @author Portfolio-3Topf Team
 * @version 1.0.0
 */

import { SimulationOptions, SteuerParameter } from '@/types';
import { OneTopfYearData, ThreeTopfYearData as YearData } from '@/types/simulation';
import { TransactionService } from './TransactionService';
import { GARCHRateCalculationIntegration, GARCHUtils } from '@/utils/garchModeling';
import { getStartOfYearMarketStatus } from '@/utils/MarketStatusUtils';
import { isStrategySuccessful } from '@/utils/successCriteria';

/**
 * Configuration for export data processing
 */
export interface ExportConfig {
  steuerParameter: SteuerParameter;
  simulationOptions: SimulationOptions;
  crisisDefinition: string;
  investmentReturnOption: string;
  inflationOption: string;
  tagesgeldOption?: string;
  zinstopfOption?: string;
}

/**
 * Service for processing simulation data for various export formats
 */
export class DataExportService {
  
  /**
   * Generate export data for 3-Topf simulation
   * Extracted from handleExport3Topf() in Schritt3.tsx
   */
  static generate3TopfExportData(
    simulationData: YearData[],
    config: ExportConfig
  ) {
    if (!simulationData || simulationData.length === 0) {
      throw new Error('No simulation data available for export');
    }

    // Check if GARCH is being used and get analysis
    const isGARCHUsed = GARCHUtils.isGARCHOption(config.investmentReturnOption);
    const garchAnalysis = isGARCHUsed
      ? GARCHRateCalculationIntegration.analyzeIndexDataForGARCH('world')
      : null;

    const exportData = {
      exportInfo: {
        title: '3-Töpfe Einzelsimulation Export',
        exportDate: new Date().toISOString(),
        simulationType: '3-Topf Einzelsimulation',
        garchEnhanced: isGARCHUsed,
        moneyFlowBreakdownDescription: {
          description:
            'Comprehensive yearly money flow analysis for each pot with detailed 3-Topf strategy descriptions',
          methodology:
            '1) Apply interest to previous balance, 2) Withdraw from Verbrauchstopf (up to inflation-adjusted target), 3) Remaining need covered by other pots, 4) Refill Verbrauchstopf if needed, 5) Post-crisis recovery: Refill Zinstopf from Investitionstopf',
          fields: {
            previousBalance: 'Balance at start of year (€)',
            interestRate: 'Interest rate applied (%)',
            interestEarned: 'Interest earned during year (€)',
            balanceAfterInterest: 'Balance after applying interest, before any transactions (€)',
            currentBalance: 'Final balance at end of year (€)',
            netChange: 'Total change during year (€)',
            inflationAdjustedTarget:
              'Inflation-adjusted annual withdrawal target (€) - Verbrauchstopf only',
            withdrawalAmount: 'Amount actually withdrawn from this pot (€) - Verbrauchstopf only',
            balanceAfterWithdrawal:
              'Balance after withdrawal, before refill (€) - Verbrauchstopf only',
            refillAmount: 'Amount refilled into pot from other sources (€) - Verbrauchstopf only',
            finalBalance: 'Alias for currentBalance - Verbrauchstopf only',
            cashFlow:
              'Net cash flow after interest (positive = inflow, negative = outflow) (€) - Zinstopf & Investitionstopf',
            cashFlowType:
              "Direction of cash flow: 'withdrawal' or 'refill' - Zinstopf & Investitionstopf",
            strategyDescriptions: 'Detailed descriptions of refill actions for each pot (topf1AuffuellQuelle, topf2AuffuellAktion)',
            transactionSummary: 'Human-readable summary of all transactions (Auffüllung, Entnahme, Rendite)',
            krisenschutzStatus: 'Zinstopf crisis protection status and target levels',
            performanceSummary: 'Investitionstopf performance analysis and strategy contribution',
          },
        },
      },
      simulationParameters: {
        steuerParameter: config.steuerParameter,
        simulationOptions: config.simulationOptions,
        crisisDefinition: config.crisisDefinition,
        actualValues: {
          retirementAge: `${config.steuerParameter?.retirementAge} Jahre`,
          berechnungBisLebensalter: `${config.steuerParameter?.berechnungBisLebensalter} Jahre`,
          zinssatzVerbrauchstopf: `${config.steuerParameter?.zinssatzVerbrauchstopf}%`,
          zinssatzZinstopf: `${config.steuerParameter?.zinssatzZinstopf}%`,
          renditeInvestitionstopf: `${config.steuerParameter?.renditeInvestitionstopf}%`,
          inflationsrate: `${config.steuerParameter?.inflationsrate}%`,
          krisendefinition: (() => {
            switch (config.crisisDefinition) {
              case 'negative-return':
                return 'Krise bei negativer Jahresrendite';
              case 'kapitalindex-drawdown':
                return 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung';
              default:
                return 'Krise bei negativer Jahresrendite';
            }
          })(),
        },
        // GARCH-specific parameters (if GARCH is used)
        ...(isGARCHUsed && garchAnalysis
          ? {
              garchModelParameters: {
                indexType: garchAnalysis.indexType,
                modelType: 'EGARCH(1,1)',
                dataPoints: garchAnalysis.dataPoints,
                methodology:
                  config.investmentReturnOption === 'historische-garch'
                    ? 'Historical returns combined with GARCH volatility clustering'
                    : 'Statistical distribution with time-varying GARCH volatility',
                calibratedParameters: {
                  volatilityPersistence: garchAnalysis.garchParameters.volatilityPersistence,
                  archEffect: garchAnalysis.garchParameters.archEffect,
                  leverageEffect: garchAnalysis.garchParameters.leverageEffect,
                },
                historicalStatistics: {
                  mean: garchAnalysis.historicalMean,
                  volatility: garchAnalysis.historicalVolatility,
                  skewness: garchAnalysis.skewness,
                  kurtosis: garchAnalysis.kurtosis,
                },
              },
            }
          : {}),
      },
      simulationResults: simulationData.map((item, index) => {
        // Use actual capital index from simulation data (supports both crisis modes)
        const kapitalIndex = item.kapitalIndex || (() => {
          // Fallback calculation for backward compatibility (year 0 = 100 baseline)
          let calculatedIndex = 100;
          for (let i = 1; i <= index; i++) {
            const yearReturn = simulationData[i].investitionstopfRendite / 100;
            calculatedIndex *= 1 + yearReturn;
          }
          return calculatedIndex;
        })();

        // Get previous year for start balances and market status calculation
        const previousYear = index > 0 ? simulationData[index - 1] : null;
        const isFirstYear = !previousYear;

        // Calculate start-of-year market status for dual timing in JSON export
        const startOfYearMarketStatus = getStartOfYearMarketStatus(
          item as any, // Cast to match YearData interface
          previousYear as any, // Cast to match YearData interface
          simulationData as any[], // Cast to match YearData array
          config.crisisDefinition as 'negative-return' | 'kapitalindex-drawdown' || 'negative-return'
        );

        // Use TransactionService for consistent transaction calculation and corrected balances
        const verbrauchstopfTransactionData = TransactionService.calculateVerbrauchstopfTransactions(
          item as any,
          previousYear as any,
          isFirstYear
        );

        const zinstopfTransactionData = TransactionService.calculateZinstopfTransactions(
          item as any,
          previousYear as any,
          isFirstYear
        );

        const investitionstopfTransactionData = TransactionService.calculateInvestitionstopfTransactions(
          item as any,
          previousYear as any,
          isFirstYear
        );

        // Use corrected balances from TransactionService instead of raw simulation data
        const verbrauchstopfData = {
          displayBalance: verbrauchstopfTransactionData.endBalance,
          startBalance: verbrauchstopfTransactionData.startBalance,
          endBalance: verbrauchstopfTransactionData.endBalance,
          zinssatz: item.verbrauchstopfZinssatz,
          transactions: verbrauchstopfTransactionData.transactions,
        };

        const zinstopfData = {
          displayBalance: zinstopfTransactionData.endBalance,
          startBalance: zinstopfTransactionData.startBalance,
          endBalance: zinstopfTransactionData.endBalance,
          zinssatz: item.zinstopfZinssatz,
          transactions: zinstopfTransactionData.transactions,
        };

        const investitionstopfData = {
          displayBalance: investitionstopfTransactionData.endBalance,
          startBalance: investitionstopfTransactionData.startBalance,
          endBalance: investitionstopfTransactionData.endBalance,
          rendite: item.investitionstopfRendite,
          transactions: investitionstopfTransactionData.transactions,
        };

        // Return simple JSON structure - just what's displayed on screen
        return {
          // Basic year data - exactly as displayed on screen
          jahr: item.jahr,
          alter: item.alter,
          gesamtentnahme: item.gesamtentnahme,
          gesamtsaldo: item.gesamtsaldo,
          inflationsrate: item.inflationsrate,
          
          // Legacy crisis indicator (kept for UI compatibility)
          istKrise: item.marktSituation === 'Krise',
          
          // Dual timing market status for enhanced analysis
          marktlage: {
            startOfYear: startOfYearMarketStatus, // 01.01 decision status
            endOfYear: item.marktSituation,       // 31.12 actual status
            decisionBasedOn: config.crisisDefinition === 'negative-return' ? 'current-year-return' : 'previous-year-capital-index',
            timingConsistent: startOfYearMarketStatus === item.marktSituation
          },
          
          auffuellAktion: item.auffuellAktion,

          // Strategy descriptions
          topf1AuffuellQuelle: (item as any).topf1AuffuellQuelle || 'Keine Auffüllung',
          topf2AuffuellAktion: (item as any).topf2AuffuellAktion || 'Keine Auffüllung',

          // Capital index details (comprehensive - replaces standalone kapitalIndex field)
          kapitalIndexDetails: {
            startOfYear: index > 0 ? Math.round(simulationData[index - 1].kapitalIndex || (() => {
              let calculatedIndex = 100;
              for (let i = 1; i < index; i++) {
                const yearReturn = simulationData[i].investitionstopfRendite / 100;
                calculatedIndex *= 1 + yearReturn;
              }
              return calculatedIndex;
            })()) : 100,
            endOfYear: Math.round(kapitalIndex),
            yearlyChange: index > 0 ? Math.round(kapitalIndex - (simulationData[index - 1].kapitalIndex || 100)) : Math.round(kapitalIndex - 100),
            yearlyChangePercent: index > 0 ? Number(((kapitalIndex / (simulationData[index - 1].kapitalIndex || 100) - 1) * 100).toFixed(2)) : Number(((kapitalIndex / 100 - 1) * 100).toFixed(2)),
          },

          // Crisis state information (for new crisis modes)
          crisisState: item.crisisState ? {
            isInCrisis: item.crisisState.isInCrisis,
            crisisStartYear: item.crisisState.crisisStartYear,
            precrisisKapitalIndex: item.crisisState.precrisisKapitalIndex,
            crisisType: item.crisisState.crisisType,
          } : undefined,

          // Simple pot data - exactly what's displayed on screen
          verbrauchstopf: verbrauchstopfData,
          zinstopf: zinstopfData,
          investitionstopf: investitionstopfData,

          // Raw simulation data for reference - rates only (balances available in structured pot objects)
          rawData: {
            verbrauchstopfZinssatz: item.verbrauchstopfZinssatz,
            zinstopfZinssatz: item.zinstopfZinssatz,
            investitionstopfRendite: item.investitionstopfRendite,
            // Include any additional fields from the simulation
            // BUG FIX: Use !== undefined checks so legitimate 0 values are not dropped
            ...((item as any).entnahmeVerbrauchstopf !== undefined && { entnahmeVerbrauchstopf: (item as any).entnahmeVerbrauchstopf }),
            ...((item as any).entnahmeZinstopf !== undefined && { entnahmeZinstopf: (item as any).entnahmeZinstopf }),
            ...((item as any).entnahmeInvestitionstopf !== undefined && { entnahmeInvestitionstopf: (item as any).entnahmeInvestitionstopf }),
            ...((item as any).initialVerbrauchstopfSaldo !== undefined && { initialVerbrauchstopfSaldo: (item as any).initialVerbrauchstopfSaldo }),
            ...((item as any).initialZinstopfSaldo !== undefined && { initialZinstopfSaldo: (item as any).initialZinstopfSaldo }),
            ...((item as any).initialInvestitionstopfSaldo !== undefined && { initialInvestitionstopfSaldo: (item as any).initialInvestitionstopfSaldo }),
            // Actual interest earned (calculated by simulation engine)
            ...((item as any).verbrauchstopfInterest !== undefined && { verbrauchstopfInterest: (item as any).verbrauchstopfInterest }),
            ...((item as any).zinstopfInterest !== undefined && { zinstopfInterest: (item as any).zinstopfInterest }),
            ...((item as any).investitionstopfInterest !== undefined && { investitionstopfInterest: (item as any).investitionstopfInterest }),
            // Zinstopf overflow transfer to Verbrauchstopf
            ...((item as any).zinstopfUeberschussTransfer !== undefined && { zinstopfUeberschussTransfer: (item as any).zinstopfUeberschussTransfer }),
          },
        };
      }),
      summary: {
        totalYears: simulationData.length,
        finalBalance: simulationData[simulationData.length - 1]?.gesamtsaldo || 0,
        totalWithdrawals: simulationData.reduce((sum, year) => sum + year.gesamtentnahme, 0),
        // Verfeinerte Erfolgs-Definition: Zielalter erreicht UND >= 95 % der Zieljahr-
        // Entnahme bedient (zentral in isStrategySuccessful), statt bisher nur Endsaldo > 0.
        successfulStrategy: isStrategySuccessful(
          simulationData,
          config.steuerParameter.berechnungBisLebensalter - config.steuerParameter.retirementAge
        ),
      },
      simulationOptions: {
        inflationOption: config.inflationOption,
        investmentReturnOption: config.investmentReturnOption,
        tagesgeldOption: config.tagesgeldOption,
        zinstopfOption: config.zinstopfOption,
        crisisDefinition: config.crisisDefinition,
      },
    };

    return exportData;
  }

  /**
   * Generate export data for 1-Topf simulation
   * Extracted from handleExport1Topf() in Schritt3.tsx
   */
  static generate1TopfExportData(
    oneTopfSimulationData: OneTopfYearData[],
    config: ExportConfig
  ) {
    if (!oneTopfSimulationData || oneTopfSimulationData.length === 0) {
      throw new Error('No 1-Topf simulation data available for export');
    }

    // Check if GARCH is being used for 1-Topf
    const is1TopfGARCHUsed = GARCHUtils.isGARCHOption(config.investmentReturnOption);
    const garchAnalysis = is1TopfGARCHUsed
      ? GARCHRateCalculationIntegration.analyzeIndexDataForGARCH('world')
      : null;

    const exportData = {
      exportInfo: {
        title: '1-Topf Einzelsimulation Export',
        exportDate: new Date().toISOString(),
        simulationType: '1-Topf Einzelsimulation',
        garchEnhanced: is1TopfGARCHUsed,
      },
      simulationParameters: {
        steuerParameter: config.steuerParameter,
        simulationOptions: {
          inflationOption: config.inflationOption,
          investmentReturnOption: config.investmentReturnOption,
        },
        crisisDefinition: config.crisisDefinition,
        actualValues: {
          retirementAge: `${config.steuerParameter?.retirementAge} Jahre`,
          berechnungBisLebensalter: `${config.steuerParameter?.berechnungBisLebensalter} Jahre`,
          renditeInvestitionstopf: `${config.steuerParameter?.renditeInvestitionstopf}%`,
          inflationsrate: `${config.steuerParameter?.inflationsrate}%`,
          krisendefinition: (() => {
            switch (config.crisisDefinition) {
              case 'negative-return':
                return 'Krise bei negativer Jahresrendite';
              case 'kapitalindex-drawdown':
                return 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung';
              default:
                return 'Krise bei negativer Jahresrendite';
            }
          })(),
        },
        // GARCH-specific parameters (if GARCH is used)
        ...(is1TopfGARCHUsed && garchAnalysis
          ? {
              garchModelParameters: {
                indexType: garchAnalysis.indexType,
                modelType: 'EGARCH(1,1)',
                dataPoints: garchAnalysis.dataPoints,
                methodology:
                  config.investmentReturnOption === 'historische-garch'
                    ? 'Historical returns combined with GARCH volatility clustering'
                    : 'Statistical distribution with time-varying GARCH volatility',
                calibratedParameters: {
                  volatilityPersistence: garchAnalysis.garchParameters.volatilityPersistence,
                  archEffect: garchAnalysis.garchParameters.archEffect,
                  leverageEffect: garchAnalysis.garchParameters.leverageEffect,
                },
                historicalStatistics: {
                  mean: garchAnalysis.historicalMean,
                  volatility: garchAnalysis.historicalVolatility,
                  skewness: garchAnalysis.skewness,
                  kurtosis: garchAnalysis.kurtosis,
                },
              },
            }
          : {}),
      },
      simulationResults: oneTopfSimulationData.map((item, index) => {
        // Use actual capital index from simulation data (supports both crisis modes)
        const kapitalIndex = item.kapitalIndex || (() => {
          // Fallback calculation for backward compatibility (year 0 = 100 baseline)
          let calculatedIndex = 100;
          for (let i = 1; i <= index; i++) {
            const yearReturn = oneTopfSimulationData[i].investitionstopfRendite / 100;
            calculatedIndex *= 1 + yearReturn;
          }
          return calculatedIndex;
        })();

        // Get previous year for transaction calculations and market status
        const previousYear = index > 0 ? oneTopfSimulationData[index - 1] : null;
        const isFirstYear = !previousYear;

        // Calculate start-of-year market status for dual timing in 1-Topf JSON export
        const startOfYearMarketStatus1Topf = getStartOfYearMarketStatus(
          item as any, // Cast to match YearData interface
          previousYear as any, // Cast to match YearData interface
          oneTopfSimulationData as any[], // Cast to match YearData array
          config.crisisDefinition as 'negative-return' | 'kapitalindex-drawdown' || 'negative-return'
        );

        // Calculate transaction details to match screen table
        // BUG FIX: No invented fallback capital - if the value is 0 or missing, use 0
        const startBalance = isFirstYear
          ? (config.steuerParameter?.vorhandenesKapital ?? 0) // Use initial capital for first year
          : previousYear.investitionstopfSaldo;

        const withdrawalAmount = item.gesamtentnahme || 0; // Always include withdrawal amount
        // TIMING FIX: The engine now withdraws at the START of the year and applies the
        // return only to the remaining balance - mirror that order here
        const interestEarned = (startBalance - withdrawalAmount) * (item.investitionstopfRendite / 100);

        // Build transaction descriptions to match screen table
        const transactions = [];
        transactions.push(`01.01.: ${Math.round(startBalance).toLocaleString('de-DE')} €`);
        
        if (withdrawalAmount > 0.01) {
          transactions.push(`- (Entnahme) ${Math.round(withdrawalAmount).toLocaleString('de-DE')} €`);
        }
        
        if (Math.abs(interestEarned) >= 0.01) {
          const sign = interestEarned >= 0 ? '+' : '-';
          transactions.push(`${sign} (Rendite) ${Math.abs(Math.round(interestEarned)).toLocaleString('de-DE')} €`);
        } else if (interestEarned === 0) {
          transactions.push(`+ (Rendite) 0 €`);
        }
        
        transactions.push(`31.12.: ${Math.round(item.investitionstopfSaldo).toLocaleString('de-DE')} €`);

        // Destructure to exclude marktSituation since we now use marktlage
        const { marktSituation, ...itemWithoutMarktSituation } = item;
        
        return {
          ...itemWithoutMarktSituation,
          
          // Dual timing market status for 1-Topf enhanced analysis (replaces legacy marktSituation and istKrise)
          marktlage: {
            startOfYear: startOfYearMarketStatus1Topf, // 01.01 decision status
            endOfYear: marktSituation || 'Hoch',         // 31.12 actual status
            decisionBasedOn: config.crisisDefinition === 'negative-return' ? 'current-year-return' : 'previous-year-capital-index',
            timingConsistent: startOfYearMarketStatus1Topf === (marktSituation || 'Hoch')
          },
          
          // Capital index details (comprehensive - replaces standalone kapitalIndex field)
          kapitalIndexDetails: {
            startOfYear: index > 0 ? Math.round(oneTopfSimulationData[index - 1].kapitalIndex || (() => {
              let calculatedIndex = 100;
              for (let i = 1; i < index; i++) {
                const yearReturn = oneTopfSimulationData[i].investitionstopfRendite / 100;
                calculatedIndex *= 1 + yearReturn;
              }
              return calculatedIndex;
            })()) : 100,
            endOfYear: Math.round(kapitalIndex),
            yearlyChange: index > 0 ? Math.round(kapitalIndex - (oneTopfSimulationData[index - 1].kapitalIndex || 100)) : Math.round(kapitalIndex - 100),
            yearlyChangePercent: index > 0 ? Number(((kapitalIndex / (oneTopfSimulationData[index - 1].kapitalIndex || 100) - 1) * 100).toFixed(2)) : Number(((kapitalIndex / 100 - 1) * 100).toFixed(2)),
          },
          
          // Crisis state information (for new crisis modes)
          crisisState: item.crisisState ? {
            isInCrisis: item.crisisState.isInCrisis,
            crisisStartYear: item.crisisState.crisisStartYear,
            precrisisKapitalIndex: item.crisisState.precrisisKapitalIndex,
            crisisType: item.crisisState.crisisType,
          } : undefined,
          
          investitionstopf: {
            startBalance: Math.round(startBalance),
            endBalance: Math.round(item.investitionstopfSaldo),
            interestRate: item.investitionstopfRendite,
            interestEarned: Math.round(interestEarned),
            withdrawalAmount: Math.round(withdrawalAmount),
            transactions: transactions,
          },
        };
      }),
      summary: {
        totalYears: oneTopfSimulationData.length,
        finalBalance: oneTopfSimulationData[oneTopfSimulationData.length - 1]?.gesamtkapital || 0,
        totalWithdrawals: oneTopfSimulationData.reduce((sum, year) => sum + year.entnahme, 0),
        // Verfeinerte Erfolgs-Definition: Zielalter erreicht UND >= 95 % der Zieljahr-
        // Entnahme bedient (zentral in isStrategySuccessful), statt bisher nur Endsaldo > 0.
        successfulStrategy: isStrategySuccessful(
          oneTopfSimulationData,
          config.steuerParameter.berechnungBisLebensalter - config.steuerParameter.retirementAge
        ),
      },
      simulationOptions: {
        inflationOption: config.inflationOption,
        investmentReturnOption: config.investmentReturnOption,
        tagesgeldOption: config.tagesgeldOption || 'parameter-rendite',
        zinstopfOption: config.zinstopfOption || 'parameter-rendite',
        crisisDefinition: config.crisisDefinition,
      },
    };

    return exportData;
  }
}
